import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If user is Super Admin, no need to load business settings
    if (user?.role === 'Super Admin') {
      console.log('Super Admin detected - skipping business settings');
      setBusiness({
        id: null,
        business_name: 'Super Admin',
        business_email: user.email
      });
      setSettings({
        currency: 'KES',
        timezone: 'Africa/Nairobi'
      });
      setLoading(false);
      return;
    }

    // For regular users, load their business settings
    if (user?.business_id) {
      loadBusinessSettings();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadBusinessSettings = async () => {
    try {
      console.log('Loading business settings for ID:', user.business_id);
      const response = await api.get(`/settings.php/business/${user.business_id}`);
      setBusiness(response.data.business);
      setSettings(response.data.settings || {});
    } catch (error) {
      console.error('Failed to load business settings:', error);
      // Don't throw - just log the error
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const response = await api.post('/settings.php', newSettings);
      if (response.data.success) {
        setSettings(prev => ({ ...prev, ...newSettings }));
        return true;
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
    return false;
  };

  const value = {
    business,
    settings,
    loading,
    updateSettings,
    isSuperAdmin: user?.role === 'Super Admin'
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};